
import React, { useState, useMemo } from 'react';
import { Star, MessageSquare, ChevronLeft, ChevronRight, User, Calendar, Smile, Send, StarHalf, ThumbsUp, CheckCircle2 } from 'lucide-react';
import { Rating, UserRole } from '../types';

interface ShopRatingProps {
  ratings: Rating[];
  userRole: UserRole;
  onSubmitRating?: (stars: number, comment: string) => void;
  isRated?: boolean;
}

export const ShopRating: React.FC<ShopRatingProps> = ({ ratings, userRole, onSubmitRating, isRated }) => {
  const [hoverStars, setHoverStars] = useState(0);
  const [selectedStars, setSelectedStars] = useState(0);
  const [comment, setComment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Calculate statistics
  const stats = useMemo(() => {
    if (ratings.length === 0) return { avg: 0, count: 0, breakdown: [0, 0, 0, 0, 0] };
    const breakdown = [0, 0, 0, 0, 0];
    let sum = 0;
    ratings.forEach(r => {
      sum += r.stars;
      if (r.stars >= 1 && r.stars <= 5) breakdown[r.stars - 1]++;
    });
    return {
      avg: parseFloat((sum / ratings.length).toFixed(1)),
      count: ratings.length,
      breakdown: [...breakdown].reverse() // 5 to 1
    };
  }, [ratings]);

  const totalPages = Math.ceil(ratings.length / itemsPerPage);
  const paginatedRatings = useMemo(() => {
    return [...ratings]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [ratings, currentPage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStars > 0 && onSubmitRating) {
      onSubmitRating(selectedStars, comment);
      setSelectedStars(0);
      setComment('');
    }
  };

  const isCustomer = userRole === UserRole.CUSTOMER;

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      {/* Review Summary Header */}
      <div className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-100 shadow-sm flex flex-col md:flex-row gap-10">
        <div className="md:w-1/3 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-slate-50 pb-8 md:pb-0 md:pr-10">
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Overall Score</p>
           <h4 className="text-7xl font-black text-slate-900 tracking-tighter mb-2">{stats.avg}</h4>
           <div className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} size={20} className={s <= Math.round(stats.avg) ? 'text-amber-400 fill-amber-400' : 'text-slate-100 fill-slate-100'} />
              ))}
           </div>
           <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Based on {stats.count} reviews</p>
        </div>
        
        <div className="flex-1 space-y-3">
           {stats.breakdown.map((count, i) => {
             const starLevel = 5 - i;
             const percentage = stats.count > 0 ? (count / stats.count) * 100 : 0;
             return (
               <div key={starLevel} className="flex items-center gap-4 group">
                  <span className="text-[10px] font-black text-slate-400 w-4">{starLevel}</span>
                  <div className="flex-1 h-2.5 bg-slate-50 rounded-full overflow-hidden relative">
                     <div 
                       className="absolute inset-y-0 left-0 bg-amber-400 rounded-full transition-all duration-1000 ease-out"
                       style={{ width: `${percentage}%` }}
                     />
                  </div>
                  <span className="text-[10px] font-black text-slate-300 w-8 text-right group-hover:text-slate-900 transition-colors">{count}</span>
               </div>
             );
           })}
        </div>
      </div>

      {/* Customer Submission Section */}
      {isCustomer && onSubmitRating && (
        <div className={`bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-xl shadow-slate-200/40 transition-all duration-500 ${isRated ? 'opacity-50 pointer-events-none grayscale' : ''}`}>
          {isRated ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
               <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 size={32} />
               </div>
               <h3 className="text-xl font-black text-slate-900">Thank you for your feedback!</h3>
               <p className="text-slate-400 font-bold mt-1">You have already rated this repair service.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-500 shadow-inner">
                   <Smile size={24} />
                </div>
                <div>
                   <h3 className="text-xl font-black text-slate-900 tracking-tight">How was the repair?</h3>
                   <p className="text-slate-400 text-sm font-bold">Your review helps other users find the best Cheangs.</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="flex justify-center gap-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onMouseEnter={() => setHoverStars(star)}
                      onMouseLeave={() => setHoverStars(0)}
                      onClick={() => setSelectedStars(star)}
                      className="transition-transform active:scale-90 hover:scale-110"
                    >
                      <Star 
                        size={56} 
                        className={`transition-all duration-200 drop-shadow-sm ${
                          (hoverStars || selectedStars) >= star ? 'text-amber-400 fill-amber-400' : 'text-slate-50'
                        }`}
                        strokeWidth={1.5}
                      />
                    </button>
                  ))}
                </div>

                <div className="relative group">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Describe the quality of work, the speed, and the professionalism..."
                    className="w-full p-8 rounded-[2rem] bg-slate-50 border-2 border-transparent font-bold text-slate-900 placeholder:text-slate-300 outline-none focus:ring-4 focus:ring-amber-50 focus:bg-white focus:border-amber-100 transition-all h-36 resize-none shadow-inner"
                  />
                  <div className="absolute bottom-6 right-8 text-slate-200 group-focus-within:text-amber-200 transition-colors">
                    <MessageSquare size={24} />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={selectedStars === 0}
                  className="w-full py-6 bg-slate-900 text-white rounded-[1.8rem] font-black text-base uppercase tracking-widest shadow-2xl shadow-slate-900/20 hover:bg-amber-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-3 active:scale-95 group"
                >
                  <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /> Submit My Review
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* Ratings History List */}
      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
           <h3 className="text-xl font-black text-slate-900 flex items-center gap-3">
             <ThumbsUp size={24} className="text-violet-500" /> 
             All Experiences
             <span className="text-[10px] bg-white border border-slate-100 text-slate-400 px-3 py-1 rounded-full uppercase tracking-widest">{ratings.length} Entries</span>
           </h3>
        </div>

        <div className="divide-y divide-slate-50">
          {paginatedRatings.length === 0 ? (
            <div className="py-24 text-center opacity-30 flex flex-col items-center">
               <Star size={64} className="mb-6 text-slate-200" />
               <p className="font-black uppercase text-sm tracking-[0.2em] text-slate-400">Silence in the gallery</p>
               <p className="text-xs font-bold mt-1">No reviews have been posted yet.</p>
            </div>
          ) : (
            paginatedRatings.map((rating) => (
              <div key={rating.id} className="p-8 md:p-10 hover:bg-slate-50/50 transition-colors group">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center text-white font-black text-lg shadow-xl shadow-slate-200">
                      {rating.customerName.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-black text-slate-900 text-lg tracking-tight leading-none mb-1.5">{rating.customerName}</h4>
                      <p className="text-[10px] text-slate-400 font-black uppercase flex items-center gap-2 tracking-widest">
                        <Calendar size={12} className="text-violet-400" /> {new Date(rating.createdAt).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex bg-amber-50/50 px-3 py-1.5 rounded-xl gap-0.5 border border-amber-100/50">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star 
                        key={s} 
                        size={14} 
                        className={s <= rating.stars ? 'text-amber-400 fill-amber-400' : 'text-slate-100 fill-slate-100'} 
                      />
                    ))}
                  </div>
                </div>
                <div className="pl-16 relative">
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-100 rounded-full" />
                  <p className="text-slate-600 font-bold leading-relaxed text-lg italic">"{rating.comment || 'The customer left no detailed comment, but provided a star rating.'}"</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="px-8 py-8 bg-slate-50/50 flex items-center justify-between border-t border-slate-50">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
              Page <span className="text-slate-900">{currentPage}</span> of {totalPages}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setCurrentPage(prev => Math.max(1, prev - 1)); }}
                disabled={currentPage === 1}
                className="w-12 h-12 rounded-2xl border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-900 disabled:opacity-30 transition-all shadow-sm active:scale-95"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => { setCurrentPage(prev => Math.min(totalPages, prev + 1)); }}
                disabled={currentPage === totalPages}
                className="w-12 h-12 rounded-2xl border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-900 disabled:opacity-30 transition-all shadow-sm active:scale-95"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
